import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Zap, Mail, ShoppingCart, FileText, Receipt, Key, UserPlus,
  CreditCard, Bell, Send, Loader2, Check, Settings, Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const triggerIcons = {
  order_received: ShoppingCart,
  order_confirmed: Check,
  order_ready: Bell,
  order_completed: Check,
  invoice_created: FileText,
  receipt_created: Receipt,
  password_reset: Key,
  welcome: UserPlus,
  subscription_confirmed: CreditCard,
  payment_reminder: Bell,
};

const triggerCategories = {
  orders: {
    label: 'Bestellungen',
    triggers: ['order_received', 'order_confirmed', 'order_ready', 'order_completed']
  },
  documents: {
    label: 'Dokumente',
    triggers: ['invoice_created', 'receipt_created']
  },
  account: {
    label: 'Account & Abo',
    triggers: ['password_reset', 'welcome', 'subscription_confirmed', 'payment_reminder']
  }
};

export default function AdminEmailTriggers() {
  const [loading, setLoading] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [saving, setSaving] = useState({});
  
  // Test dialog
  const [testDialog, setTestDialog] = useState(false);
  const [testTrigger, setTestTrigger] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    fetchTriggers();
  }, []);

  const fetchTriggers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/triggers`);
      if (response.ok) {
        const data = await response.json();
        setTriggers(data.triggers || []);
      }
    } catch (error) {
      console.error('Error fetching triggers:', error);
      toast.error('Fehler beim Laden der Trigger');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (triggerType, enabled) => {
    setSaving({ ...saving, [triggerType]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/email/triggers/${triggerType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      
      if (!response.ok) throw new Error('Failed to update');
      
      // Update local state
      setTriggers(triggers.map(t => 
        t.trigger_type === triggerType ? { ...t, enabled } : t
      ));
      
      toast.success(enabled ? 'Trigger aktiviert' : 'Trigger deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSaving({ ...saving, [triggerType]: false });
    }
  };

  const handleTest = (trigger) => {
    setTestTrigger(trigger);
    setTestDialog(true);
  };

  const handleSendTest = async () => {
    if (!testEmail || !testTrigger) return;
    
    setTestSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email/triggers/test/${testTrigger.trigger_type}?recipient=${encodeURIComponent(testEmail)}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success('Test-E-Mail gesendet!', {
          description: `Trigger: ${testTrigger.name}`
        });
        setTestDialog(false);
        setTestEmail('');
      } else {
        throw new Error(data.result?.message || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Senden', {
        description: error.message
      });
    } finally {
      setTestSending(false);
    }
  };

  const getTriggersByCategory = (category) => {
    return triggers.filter(t => category.triggers.includes(t.trigger_type));
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full bg-slate-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold flex items-center gap-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-triggers-title"
        >
          <Zap className="h-8 w-8 text-amber-400" />
          E-Mail-Automatisierung
        </h1>
        <p className="text-slate-400 mt-1">Automatische E-Mails bei Events konfigurieren</p>
      </div>

      {/* Info Banner */}
      <div 
        className="p-4 rounded-lg mb-6 flex items-start gap-3"
        style={{ backgroundColor: '#2F80FF20', border: '1px solid #2F80FF40' }}
      >
        <Mail className="h-5 w-5 text-[#2F80FF] mt-0.5" />
        <div>
          <p className="text-sm">
            Aktivierte Trigger senden automatisch E-Mails an Kunden oder Benutzer, 
            wenn das entsprechende Event eintritt.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            E-Mail-Einstellungen müssen unter "E-Mail" konfiguriert und aktiviert sein.
          </p>
        </div>
      </div>

      {/* Trigger Categories */}
      <div className="space-y-6">
        {Object.entries(triggerCategories).map(([key, category]) => (
          <Card 
            key={key}
            className="p-6" 
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <h2 className="text-lg font-semibold mb-4">{category.label}</h2>
            
            <div className="space-y-3">
              {getTriggersByCategory(category).map((trigger) => {
                const Icon = triggerIcons[trigger.trigger_type] || Mail;
                
                return (
                  <div 
                    key={trigger.trigger_type}
                    className="flex items-center justify-between p-4 rounded-lg"
                    style={{ backgroundColor: '#0E1628' }}
                    data-testid={`trigger-${trigger.trigger_type}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        trigger.enabled ? 'bg-[#2F80FF]/20' : 'bg-slate-700/50'
                      }`}>
                        <Icon className={`h-5 w-5 ${
                          trigger.enabled ? 'text-[#2F80FF]' : 'text-slate-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{trigger.name}</span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              trigger.enabled 
                                ? 'border-emerald-500 text-emerald-400' 
                                : 'border-slate-600 text-slate-500'
                            }`}
                          >
                            {trigger.enabled ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400">{trigger.description}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Template: <code className="bg-slate-800 px-1 rounded">{trigger.template}</code>
                          {' • '}
                          Empfänger: {trigger.recipient_type === 'customer' ? 'Kunde' : 'Benutzer'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTest(trigger)}
                        className="text-slate-400 hover:text-white"
                        disabled={!trigger.enabled}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Test
                      </Button>
                      
                      <Switch
                        checked={trigger.enabled}
                        onCheckedChange={(enabled) => handleToggle(trigger.trigger_type, enabled)}
                        disabled={saving[trigger.trigger_type]}
                        data-testid={`trigger-switch-${trigger.trigger_type}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Test Dialog */}
      <Dialog open={testDialog} onOpenChange={setTestDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-amber-400" />
              Trigger testen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Senden Sie eine Test-E-Mail für "{testTrigger?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="test-trigger-email">Empfänger E-Mail</Label>
            <Input
              id="test-trigger-email"
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="mt-1 bg-[#0E1628] border-slate-700"
              data-testid="test-trigger-email"
            />
          </div>
          
          <div className="p-3 rounded-lg bg-slate-800/50">
            <p className="text-sm text-slate-400">
              <strong>Template:</strong> {testTrigger?.template}<br />
              <strong>Betreff:</strong> Wird aus Template generiert
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={testSending || !testEmail}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="send-test-trigger-btn"
            >
              {testSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Test senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
